import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck } from "lucide-react";
import EmailCodeLogin from "@/pages/EmailCodeLogin";
import { InviteBanner } from "@/lib/referral";
import { useAuth } from "@/lib/auth";

/** The one-screen login gate. The "Continue with Google" button sends the
 *  user to Emergent's hosted OAuth URL with a dynamic redirect pointing back
 *  at the current origin + `/`. Google consent happens on Emergent's side; the
 *  user lands back at `/#session_id=<temp>` which <AuthCallback> picks up.
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 *  THIS BREAKS THE AUTH. The redirectUrl must come from window.location.origin. */
export default function Login() {
  const { authNotice } = useAuth();
  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="pt-safe-12 flex min-h-screen flex-col items-center justify-center bg-[#F8FAFC] px-4 py-12">
      <div className="w-full max-w-sm space-y-6 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="login-card">
        <InviteBanner />
        {authNotice && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="alert" data-testid="login-notice">{authNotice}</p>}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0053DA] font-mono text-sm font-bold text-white">SL</div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">SnapList</h1>
            <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">eBay Draft AI</p>
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Welcome back.</h2>
          <p className="text-sm text-slate-600">
            Sign in with Google, or with any email — including your Apple ID / iCloud email.
          </p>
        </div>

        <Button
          onClick={signInWithGoogle}
          className="h-11 w-full bg-[#0053DA] text-base font-semibold hover:bg-[#0041B0]"
          data-testid="login-google-btn"
        >
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> or use your email <span className="h-px flex-1 bg-slate-200" />
        </div>

        <EmailCodeLogin />

        <div className="space-y-2 border-t border-slate-100 pt-4 text-[11px] text-slate-500">
          <p className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            No passwords — SnapList only stores your name and email.
          </p>
          <p className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-[#0053DA]" />
            New here? Get 7 days of Pro free — no card needed.
          </p>
          <p className="pt-1 text-[10px] text-slate-400" data-testid="login-legal-consent">
            By continuing you agree to the <a href="/terms" className="underline">Terms</a>, <a href="/privacy" className="underline">Privacy Policy</a> and <a href="/refund" className="underline">Refund Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
